import { ChangeDetectionStrategy, Component, inject, NgZone, OnInit, signal, ViewChild } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { of } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';

import { ContactInformationModel } from '../../../models/contact-information.model';
import { ChangeEmailPhoneModel } from '../../../models/core/change-email-phone.model';
import { ShareholdersResource } from '../../../services/resources/shareholders.resource';
import { SharedBusinessLogicService } from '../../../services/shared-business-logic.service';

@Component({
    selector: 'sl-change-email-phone',
    templateUrl: './change-email-phone.component.html',
    styleUrls: ['./change-email-phone.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChangeEmailPhoneComponent implements OnInit {
    @ViewChild('stepper', { static: true }) private stepper: MatStepper;

    formGroup: UntypedFormGroup;
    emailOrMobileInstruction = 'Contact.EmailOrMobile';
    emailOrMobilePlaceholderKey = 'Contact.EmailOrMobile';
    readonly inputType = signal<'mobile' | 'email' | 'both'>('both');

    isSendingField = false;
    get canMoveForward(): boolean {
        return (
            this.formGroup.valid
            && !this.isSendingField
            && (this.stepper.selectedIndex === 1 ? this.formGroup.value.confirmationCode?.length > 0 : true)
        );
    }

    readonly data = inject<ChangeEmailPhoneModel>(MAT_DIALOG_DATA);

    private readonly dialogRef = inject(MatDialogRef);
    private readonly formBuilder = inject(UntypedFormBuilder);
    private readonly ngZone = inject(NgZone);
    private readonly shareholdersResource = inject(ShareholdersResource);
    private readonly sharedBusinessLogicService = inject(SharedBusinessLogicService);

    ngOnInit(): void {
        this.formGroup = this.formBuilder.group({
            emailOrMobile: new FormControl(undefined, Validators.required),
            confirmationCode: new FormControl(undefined, undefined)
        });

        this.setupLabel();
    }

    saveData(): void {
        const data = this.formGroup.value as ContactInformationModel;

        if (!data.confirmationCode) {
            this.isSendingField = true;
            this.shareholdersResource
                .updateContactInformation(data)
                .pipe(
                    tap(() => this.goToNext()),
                    finalize(() => (this.isSendingField = false))
                )
                .subscribe();
        } else {
            this.ngZone.run(() => {
                if (!!this.stepper.selected) {
                    this.stepper.selected.completed = true;
                }
                this.stepper.next(); // Moves to next step - loading screen.

                this.shareholdersResource
                    .updateContactInformation(data)
                    .pipe(
                        tap(r => {
                            if (data.confirmationCode && r.value.isSuccessful) {
                                if (!this.data.isEmailConfirmed) {
                                    this.data.isEmailConfirmed = true;

                                    if (this.data.validateEmailOnly || this.data.isPhoneConfirmed) {
                                        this.completeRequest();
                                    } else {
                                        this.setupLabel();
                                        this.formGroup.reset();
                                        this.stepper.reset();
                                    }
                                } else {
                                    this.completeRequest();
                                }
                            }
                        }),
                        catchError(() => {
                            this.goBackTo(1, false);
                            return of('');
                        })
                    )
                    .subscribe();
            });
        }
    }

    goBackTo(index = 0, resetForm = true): void {
        const previousStep = this.stepper.steps.toArray()[index];
        previousStep.completed = false;
        previousStep.editable = true;
        if (!!this.stepper.selected) {
            this.stepper.selected.completed = false;
        }

        if (resetForm) {
            this.formGroup.reset();
        }

        this.stepper.previous();

        previousStep.editable = false;
    }

    private goToNext(): void {
        if (!!this.stepper.selected) {
            this.stepper.selected.completed = true;
        }
        this.stepper.next();
    }

    private completeRequest(): void {
        this.sharedBusinessLogicService.reloadDeliveryInformation();
        this.dialogRef.close(true); // Closes the dialog - success.
    }

    private setupLabel(): void {
        if (!!this.data) {
            if (!this.data.isEmailConfirmed) {
                this.emailOrMobilePlaceholderKey = 'Contact.Email';
                this.emailOrMobileInstruction = 'DeliveryMethod.EnterYourEmail';
                this.inputType.set('email');
            } else if (!this.data.isPhoneConfirmed) {
                this.emailOrMobilePlaceholderKey = 'Contact.MobileNumber';
                this.emailOrMobileInstruction = 'DeliveryMethod.EnterYourMobile';
                this.inputType.set('mobile');
            } else {
                this.inputType.set('both');
            }
        }
    }
}
